import {
  faChevronRight,
  faEye,
  faEyeSlash,
  faQuoteLeft,
  faQuoteRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { registerUser } from '../api/signup-service.js';
import CustomButton from '../components/common/custom-button/custom-button.jsx';
import CustomCheckbox from '../components/common/custom-checkbox/custom-checkbox.jsx';
import CustomSelect from '../components/common/custom-select/custom-select.jsx';
import CustomTextInputField from '../components/common/custom-text-input-field/custom-text-input-field.jsx';
import Text from '../components/common/custom-text/custom-text.jsx';
import { subscriptionTiers } from '../constants/subscription-tiers.js';
import { timezones } from '../constants/time-constants.js';
import { trackSignupInHubspot } from '../utils/signup-utils.js';
import {
  isRequired,
  isValidEmail,
  isValidPassword,
  isValidFullName,
} from '../utils/validation-utils.js';

import '../styles/signup/signup.scss';
import { useCurrentUser } from '../store/current-user-context.jsx';

const agencySizeOptions = [
  { value: 'Just my business', label: 'Just my business' },
  { value: '1 - 2 clients', label: '1 - 2 clients' },
  { value: '3 - 5 clients', label: '3 - 5 clients' },
  { value: '6 - 10 clients', label: '6 - 10 clients' },
  { value: '11 - 25 clients', label: '11 - 25 clients' },
  { value: '26 - 50 clients', label: '26 - 50 clients' },
  { value: '51+ clients', label: '51+ clients' },
];

const SignupRegister = ({ formData = {}, isMobile = false, dispatch, trackEventOnce }) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasExistingAccount, setHasExistingAccount] = React.useState(false);
  const { refreshCurrentUser } = useCurrentUser();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const updateFieldInContext = (value, fieldName) => {
    dispatch({ type: fieldName, payload: value });
  };

  const handleNextStep = () => {
    dispatch({ type: 'UPDATE_ACTIVE_STEP', payload: isMobile ? 3 : 1 });
  };

  const isFormValid = () => {
    const {
      fullName,
      email,
      password,
      companyName,
      timezone,
      clientCount,
      agreedToTerms,
    } = formData;
    const validEmail = isValidEmail(email);
    const validPassword = isValidPassword(password);
    const validFullName = isValidFullName(fullName);

    return (
      validFullName &&
      validEmail &&
      validPassword &&
      !!companyName &&
      !!timezone &&
      !!clientCount &&
      !!agreedToTerms
    );
  };

  async function handleRecaptchaVerification() {
    if(!executeRecaptcha) {
      console.error('reCAPTCHA not initialized');
      return;
    }

    const token = await executeRecaptcha('registration_step1');
    return token;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await handleRecaptchaVerification();
      
      if (!recaptchaToken) {
        console.error('Security verification failed. Please try again.');
      }

      const payload = {
        agencySize: formData.clientCount,
        companyName: formData.companyName,
        email: formData.email,
        fullname: formData.fullName,
        password: formData.password,
        phone: formData.phoneNumber || null,
        subscriptionTier: formData.plan.value,
        tos: formData.agreedToTerms,
        timezone: formData.timezone,
        recaptchaToken
      };

      await registerUser({ payload });
      
      trackSignupInHubspot(formData);

      const newUser = await refreshCurrentUser();
      if (newUser) {
        trackEventOnce('registration_step1', {
          agency_id: newUser.agencyId,
          plan: formData.plan.name,
        });
      }

      /* Mobile register will skip workspace configuration step. 
       We still need the workspace count for the agency for pricing.
       Since a user cannot add more workspaces on mobile, we will quote them for 1. */
      if (isMobile) {
        dispatch({
          type: 'UPDATE_WORKSPACE_COUNT',
          payload: 1,
        });
      }

      handleNextStep();
    } catch (error) {
      if (error?.response?.status === 409) {
        setHasExistingAccount(true);
        return;
      }
      if (error?.response?.status === 400 && error?.response?.data?.errorReason === 'RECAPTCHA_SCORE_LOW') {
        window.location.href = '/help';
        return;
      }
      console.error('Registration failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');
    const yearlyParam = urlParams.get('yearly');

    if (planParam) {
      // External marketing sites may still link to ?plan=studio
      const normalizedPlan = planParam === 'studio' ? 'team' : planParam;
      const selectedPlan = Object.values(subscriptionTiers).find(
        plan => plan.name === normalizedPlan
      );
      if (selectedPlan) {
        updateFieldInContext(selectedPlan, 'UPDATE_PLAN');
      }
    }

    if (yearlyParam === 'true') {
      updateFieldInContext('annual', 'UPDATE_BILLING_INTERVAL');
    }
  }, []);

  return (
    <div className="register-container" data-testid={'signup-register-container'}>
      <div className="register-form">
        <section className="signup-form">
          <form onSubmit={handleSubmit}>
            <Text as="h2" variant="l1" color="#324975">
              Plan: {formData.plan.label}
            </Text>
            <header className="header-text">
              <Text as="h4" variant="h4" color="#324975">
                Let's Start With Basic Info
              </Text>
              <Text variant="b2" color="#2B4A78">
                This helps us customize your experience and make
                recommendations.
              </Text>
            </header>
            <div className="register-fields-container">
              <CustomTextInputField
                label={'Full Name'}
                value={formData?.fullName}
                onChange={event =>
                  updateFieldInContext(event.target.value, 'UPDATE_NAME')
                }
                required
                fullWidth
                defaultValidationError={'Enter your full name.'}
                validations={[
                  {
                    validation: isRequired,
                    validationError: 'Enter your full name.',
                  },
                  {
                    validation: isValidFullName,
                    validationError: 'Enter a valid full name.',
                  },
                ]}
                testid={'signup-register-full-name'}
              />
              <CustomTextInputField
                label={'Work Email Address'}
                value={formData?.email}
                onChange={event => {
                  setHasExistingAccount(false);
                  updateFieldInContext(event.target.value, 'UPDATE_EMAIL');
                }}
                required
                fullWidth
                defaultValidationError={
                  'Enter a valid email address to sign in later.'
                }
                validations={[
                  {
                    validation: isValidEmail,
                    validationError:
                      'Enter a valid email address to sign in later.',
                  },
                ]}
                testid={'signup-register-email'}
                error={hasExistingAccount}
                helperText={
                  hasExistingAccount && (
                    <Text as="span" variant="b2" color="6B7280">
                      Your email is already in use. Use your existing password
                      to <a href="/login">log in</a>. Don't remember it? No
                      problem. <a href="/login">You can reset it!</a>
                    </Text>
                  )
                }
              />
              <CustomTextInputField
                label={'Password'}
                value={formData?.password}
                onChange={event =>
                  updateFieldInContext(event.target.value, 'UPDATE_PASSWORD')
                }
                required
                fullWidth
                defaultValidationError={'Enter a valid password.'}
                validations={[
                  {
                    validation: isRequired,
                    validationError: 'Enter a valid password',
                  },
                  {
                    validation: isValidPassword,
                    validationError: 'Password must be at least 6 characters.',
                  },
                ]}
                testid={'signup-register-password'}
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <FontAwesomeIcon
                    icon={showPassword ? faEye : faEyeSlash}
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="password-toggle-icon"
                    style={{ cursor: 'pointer', padding: '8px' }}
                  />
                }
              />
              <CustomTextInputField
                label={'Phone Number'}
                value={formData?.phoneNumber}
                onChange={event =>
                  updateFieldInContext(
                    event.target.value,
                    'UPDATE_PHONE_NUMBER'
                  )
                }
                fullWidth
                testid={'signup-register-phone-number'}
              />
            </div>
            <fieldset className="company-info">
              <legend>
                <Text as="h5" variant="h5" color="#324975">
                  Company Info
                </Text>
              </legend>
              <div className="company-info-fields">
                <div className="company-name-field">
                  <CustomTextInputField
                    label={'Company Name'}
                    hideLabel={true}
                    value={formData?.companyName}
                    onChange={event =>
                      updateFieldInContext(
                        event.target.value,
                        'UPDATE_COMPANY_NAME'
                      )
                    }
                    placeholder={'Company Name'}
                    required
                    fullWidth
                    defaultValidationError={'Enter the name of your company.'}
                    validations={[
                      {
                        validation: isRequired,
                        validationError: 'Enter the name of your company.',
                      },
                    ]}
                    testid={'signup-register-company-name'}
                    autoComplete="company-name"
                  />
                </div>
                <CustomSelect
                  description={null}
                  optional={false}
                  options={timezones}
                  styles={{ minWidth: '200px', marginTop: '16px' }}
                  value={formData?.timezone}
                  onChange={event =>
                    updateFieldInContext(event.value, 'UPDATE_TIMEZONE')
                  }
                />
              </div>
            </fieldset>
            <CustomSelect
              description={
                'How many clients do you actively manage social media for?'
              }
              options={agencySizeOptions}
              value={formData?.clientCount}
              styles={{ marginTop: '16px' }}
              onChange={event =>
                updateFieldInContext(event.value, 'UPDATE_CLIENT_COUNT')
              }
              validationError={'Choose a value from the dropdown.'}
              validations={[isRequired]}
              testid={'signup-register-client-count'}
            />
            <div className="register-next-step-container">
              <CustomCheckbox
                label={
                  <>
                    I agree to the{' '}
                    <a
                      href="https://www.cloudcampaign.com/utility-pages/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      TOS
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.cloudcampaign.com/utility-pages/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                }
                checked={formData?.agreedToTerms}
                onChange={e =>
                  updateFieldInContext(
                    e.target.checked,
                    'UPDATE_AGREED_TO_TERMS'
                  )
                }
                id="tos"
                testid={'signup-register-tos'}
              />
              <CustomButton
                variant="primary"
                faIcon={faChevronRight}
                iconPosition="right"
                type="submit"
                testid={'signup-register-next'}
                disabled={!isFormValid()}
                fullWidth
                isLoading={isSubmitting}
              >
                Next Step
              </CustomButton>
            </div>
          </form>
        </section>
        <aside className="testimonial-banner-container">
          <blockquote className="testimonial-quote">
            <Text as="h3" variant="bl" color="#ffffff">
              <span>
                <FontAwesomeIcon icon={faQuoteLeft} />
              </span>{' '}
              I can confidently say that Cloud Campaign offers the best solution
              for growth-focused agencies available on the market today.{' '}
              <span>
                <FontAwesomeIcon icon={faQuoteRight} />
              </span>
            </Text>
          </blockquote>
          <img
            alt="98 buck socail logo"
            src={'../res/98-buck-social-logo-white.png'}
          />
        </aside>
      </div>
    </div>
  );
};

export default SignupRegister;
